import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldAlert, X, Eye } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { API_URL } from "../../lib/utils";

const SEV = { high: "border-red-500/60 bg-red-500/10 text-red-700 dark:text-red-300", medium: "border-amber-500/60 bg-amber-500/10 text-amber-700 dark:text-amber-300" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" }) : "");

// Aviso ao gerente/admin: logins falhados repetidos, bloqueios e acessos de IP novo (últimas 24h)
export const SecurityAlertsBanner = ({ enabled }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => axios.get(`${API_URL}/security/alerts`, { withCredentials: true }).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { if (enabled) load(); }, [enabled]);

  if (!enabled || !data?.unseen_count) return null;
  const high = data.alerts.some((a) => a.severity === "high" && a.at > (data.ack_at || ""));
  const ack = async () => {
    try {
      await axios.post(`${API_URL}/security/alerts/ack`, {}, { withCredentials: true });
      setOpen(false);
      load();
      toast.success("Alertas marcados como vistos");
    } catch { toast.error("Erro ao atualizar alertas"); }
  };

  return (
    <>
      <div className={`rounded-xl border px-4 py-3 flex flex-wrap items-center gap-3 ${SEV[high ? "high" : "medium"]}`} data-testid="security-alerts-banner">
        <ShieldAlert className="w-5 h-5 shrink-0" />
        <div className="flex-1 min-w-[200px]">
          <p className="text-sm font-semibold" data-testid="security-alerts-title">{data.unseen_count} alerta(s) de segurança nas últimas 24h</p>
          <p className="text-xs opacity-80 truncate">{data.alerts[0]?.title} — {data.alerts[0]?.detail}</p>
        </div>
        <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => setOpen(true)} data-testid="security-alerts-view-btn"><Eye className="w-3.5 h-3.5" /> Ver detalhes</Button>
        <Button size="sm" variant="ghost" className="h-8" onClick={ack} data-testid="security-alerts-ack-btn"><X className="w-4 h-4" /></Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="security-alerts-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-red-500" /> Alertas de segurança</DialogTitle>
            <DialogDescription>Tentativas de login falhadas repetidas, bloqueios por força bruta e acessos a partir de IPs nunca antes usados (24h).</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {data.alerts.map((a, i) => (
              <div key={i} className={`rounded-lg border p-3 ${SEV[a.severity] || SEV.medium}`} data-testid={`security-alert-${i}`}>
                <div className="flex justify-between gap-2 text-sm font-semibold"><span>{a.title}</span><span className="text-xs font-normal opacity-70 whitespace-nowrap">{fmt(a.at)}</span></div>
                <p className="text-xs mt-1 opacity-90 break-words">{a.detail}</p>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Fechar</Button>
            <Button onClick={ack} data-testid="security-alerts-ack-dialog-btn">Marcar como visto</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
